import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { useUpdateProfile, useUploadOwnPhoto } from '../hooks';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Calendar, LogOut, Upload, X } from 'lucide-react';
import { TimezoneSelect } from '../components/ui/timezone-select';
import { dateToISOString, normalizeDateValue } from '../utils/formatters';

const Onboarding: React.FC = () => {
  const { t } = useTranslation('onboarding');
  const { user, signOut, refreshUser } = useAuth();
  const navigate = useNavigate();
  const updateProfile = useUpdateProfile();
  const uploadPhoto = useUploadOwnPhoto();
  const [birthDate, setBirthDate] = useState('');
  const [timezone, setTimezone] = useState('');
  const [photo, setPhoto] = useState<File | null>(null);
  const [photoPreview, setPhotoPreview] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    // If user already has birth date, redirect to dashboard
    // Photo is optional, so we only check for birthDate
    if (user?.birthDate) {
      navigate('/dashboard');
    }
  }, [user, navigate]);

  const handlePhotoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        setError(t('error.photoTooLarge'));
        return;
      }
      setPhoto(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setPhotoPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemovePhoto = () => {
    setPhoto(null);
    setPhotoPreview(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!birthDate) {
      setError(t('error.birthDateRequired'));
      return;
    }

    try {
      setError(null);

      // Update birth date and timezone
      await updateProfile.mutateAsync({
        birthDate: dateToISOString(birthDate),
        ...(timezone && { timezone }),
      });

      // Upload photo if provided
      if (photo) {
        await uploadPhoto.mutateAsync(photo);
      }

      // Refresh user data to get the updated photoURL
      await refreshUser();

      setSuccess(true);
      
      // Redirect to dashboard after a short delay
      setTimeout(() => {
        navigate('/dashboard');
      }, 1500);
    } catch (err: any) {
      setError(err.response?.data?.message || t('error.failed'));
    }
  };

  const handleSkip = async () => {
    try {
      setError(null);
      
      // Set a placeholder birthdate (Jan 1, 1900) to mark onboarding as complete
      // This allows users to skip but still marks them as having completed onboarding
      await updateProfile.mutateAsync({
        birthDate: dateToISOString('1900-01-01'),
      });
      
      await refreshUser();
      navigate('/dashboard');
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to skip onboarding');
    }
  };

  const handleSignOut = async () => {
    await signOut();
    navigate('/login');
  };

  const loading = updateProfile.isPending || uploadPhoto.isPending;

  return (
    <div className="min-h-screen bg-pure-white flex items-center justify-center p-4 relative">
      <Button
        variant="ghost"
        size="icon"
        onClick={handleSignOut}
        className="absolute top-4 right-4 text-almost-black/60"
        title="Sign Out"
      >
        <LogOut className="h-5 w-5" />
      </Button>
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="mx-auto mb-4 w-16 h-16 bg-prosigliere-blue/10 rounded-full flex items-center justify-center">
            <Calendar className="h-8 w-8 text-prosigliere-blue" />
          </div>
          <CardTitle className="text-2xl">{t('title')}</CardTitle>
          <CardDescription className="text-base">
            {t('description')}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {error && (
            <Alert variant="destructive" className="mb-4">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {success && (
            <Alert className="mb-4 border-green-500 bg-green-50">
              <AlertDescription className="text-green-700">
                {t('success')}
              </AlertDescription>
            </Alert>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="photo">{t('form.labels.profilePhoto')}</Label>
              {photoPreview ? (
                <div className="relative">
                  <img
                    src={photoPreview}
                    alt="Preview"
                    className="w-32 h-32 rounded-full object-cover mx-auto border-4 border-prosigliere-blue/20"
                  />
                  <button
                    type="button"
                    onClick={handleRemovePhoto}
                    className="absolute top-0 right-1/2 translate-x-16 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                    disabled={loading || success}
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              ) : (
                <div className="flex items-center justify-center w-full">
                  <label
                    htmlFor="photo"
                    className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed rounded-lg cursor-pointer bg-almost-black/5 hover:bg-almost-black/10 transition-colors"
                  >
                    <div className="flex flex-col items-center justify-center pt-5 pb-6">
                      <Upload className="h-8 w-8 text-almost-black/40 mb-2" />
                      <p className="text-sm text-almost-black/60">{t('form.upload.click')}</p>
                      <p className="text-xs text-almost-black/40">{t('form.upload.formats')}</p>
                    </div>
                    <input
                      id="photo"
                      type="file"
                      className="hidden"
                      accept="image/jpeg,image/jpg,image/png,image/webp"
                      onChange={handlePhotoChange}
                      disabled={loading || success}
                    />
                  </label>
                </div>
              )}
              <p className="text-xs text-almost-black/60">
                {t('form.hints.photo')}
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="timezone">Timezone</Label>
              <TimezoneSelect
                value={timezone}
                onChange={setTimezone}
                disabled={loading || success}
              />
              <p className="text-xs text-almost-black/60">
                Used for celebrations and notifications at the right local time
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="birthDate">{t('form.labels.birthDate')}</Label>
              <Input
                id="birthDate"
                type="date"
                value={birthDate}
                onChange={(e) => setBirthDate(normalizeDateValue(e.target.value))}
                max={new Date().toISOString().split('T')[0]}
                disabled={loading || success}
                className="w-full"
              />
              <p className="text-xs text-almost-black/60">
                {t('form.hints.birthDate')}
              </p>
            </div>

            <div className="flex flex-col gap-2">
              <Button
                type="submit"
                disabled={loading || success || !birthDate}
                className="w-full"
              >
                {loading ? t('buttons.submitting') : t('buttons.submit')}
              </Button>
              <Button
                type="button"
                variant="ghost"
                onClick={handleSkip}
                disabled={loading || success}
                className="w-full"
              >
                {t('buttons.skip')}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default Onboarding;
